import React from 'react';
import { Box, Typography, Avatar, Chip, Alert } from '@mui/material';
import { ReportProblem as ReportIcon, Circle as CircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export default function ModerationReportNotificationCard({ notification, onMarkRead }) {
  const navigate = useNavigate();
  const payload = notification.payload || {};

  const handleClick = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (payload.target_type === 'call' && payload.target_id) {
      navigate(`/funding/call/${payload.target_id}`);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        py: 1.25,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : '#fff3e0',
        cursor: payload.target_id ? 'pointer' : 'default',
        transition: 'background-color 0.2s',
        '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'error.main', flexShrink: 0 }}>
          <ReportIcon sx={{ fontSize: 18 }} />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={700} color="error.dark">
              Comment reported
            </Typography>
            <Chip
              label="Needs review"
              size="small"
              color="error"
              variant="outlined"
              sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Reported by{' '}
            <Box component="span" fontWeight={600} color="text.primary">
              {payload.reporter_name || 'A user'}
            </Box>
            {payload.comment_author_name && (
              <>
                {' · comment by '}
                <Box component="span" fontWeight={600} color="text.primary">
                  {payload.comment_author_name}
                </Box>
              </>
            )}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
          </Typography>
          {!notification.is_read && (
            <CircleIcon sx={{ fontSize: 8, color: 'error.main' }} />
          )}
        </Box>
      </Box>

      {/* Reason */}
      {payload.reason_label && (
        <Alert
          severity="error"
          icon={false}
          sx={{ py: 0.75, px: 1.25, mb: 1, borderRadius: 1, '& .MuiAlert-message': { p: 0 } }}
        >
          <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.25 }}>
            Reason: {payload.reason_label}
          </Typography>
          {payload.reason_detail && (
            <Typography variant="caption" color="text.secondary">
              {payload.reason_detail}
            </Typography>
          )}
        </Alert>
      )}

      {/* Comment preview */}
      {payload.comment_preview && (
        <Typography
          variant="body2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 0.5,
          }}
        >
          "{payload.comment_preview}{payload.comment_preview.length >= 150 ? '…' : ''}"
        </Typography>
      )}

      {payload.target_type === 'call' && (
        <Typography variant="caption" color="primary.main">
          Click to view the funding call →
        </Typography>
      )}
    </Box>
  );
}
